import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  DateTimeField,
  Checkbox,
  RadioButtons,
  Modal,
  MODAL_ID
} from "../../shared-components";
import { Button, ButtonVariant } from "@wattpad/web-ui-library";
import { useTrans } from "../../hooks";
import moment from "moment";

export const PUBLISH_TYPE = {
  Now: 0,
  Scheduled: 1
};
const PUBLISH_INTERVAL_MINUTES = 15;

const PublishModal = ({
  onPublish,
  onClose,
  scheduledPublishDatetime,
  triggerSchedulePart,
  onUnschedule
}) => {
  const modalRef = useRef();
  const { trans } = useTrans();
  const [publishType, setPublishType] = useState(
    scheduledPublishDatetime ? PUBLISH_TYPE.Scheduled : PUBLISH_TYPE.Now
  );
  const [publishConfirmed, setPublishConfirmed] = useState(
    scheduledPublishDatetime ? true : false
  );
  const [initialDate] = useState(scheduledPublishDatetime);
  const [publishDate, setPublishDate] = useState(scheduledPublishDatetime);
  const [isValidDate, setIsValidDate] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  let momentLocalized = moment().locale("en");

  const handleTypeSelect = useCallback(
    event => {
      const { value } = event.target;
      setPublishType(parseInt(value));
    },
    [setPublishType]
  );

  useEffect(
    () => {
      if (!triggerSchedulePart) return;
      modalRef.current.show();
    },
    [triggerSchedulePart, modalRef]
  );

  useEffect(
    () => {
      const minDate = moment()
        .add(PUBLISH_INTERVAL_MINUTES, "minutes")
        .toISOString();
      setIsValidDate(minDate <= publishDate);
    },
    [publishDate]
  );

  const handlePublish = event => {
    onPublish && onPublish(event, publishType);
    modalRef.current.hide();
  };

  const handleSchedule = () => {
    onPublish &&
      onPublish({ value: publishDate }, publishType, setErrorMessage);
  };

  const handleUnschedule = () => {
    onUnschedule && onUnschedule(setErrorMessage);
  };

  const handleOnClose = () => {
    setIsValidDate(false);
    setErrorMessage("");
    setPublishDate(initialDate);
    setPublishConfirmed(!!initialDate);
    onClose && onClose();
  };

  return (
    <Modal
      id={MODAL_ID.PUBLISH_MODAL_ID}
      {...{ onClose: handleOnClose }}
      ref={modalRef}
    >
      <div className="publish-modal-content">
        <h2 className="modal-title">{trans("Publish your part")}</h2>

        <RadioButtons
          name="publish_type"
          options={[
            {
              label: trans("Publish now"),
              value: PUBLISH_TYPE.Now
            },
            {
              label: trans("Schedule to publish later"),
              value: PUBLISH_TYPE.Scheduled
            }
          ]}
          value={publishType}
          onChange={handleTypeSelect}
        />

        {publishType == PUBLISH_TYPE.Now && (
          <div className="publish-footer">
            <Button fullWidth={true} onClick={handlePublish}>
              {trans("Publish")}
            </Button>
          </div>
        )}
        {publishType == PUBLISH_TYPE.Scheduled && (
          <div className="publish-schedule">
            <label className="schedule-date-label">
              {trans("Publish Date & Time")}:
            </label>

            <DateTimeField
              className="schedule-date"
              value={scheduledPublishDatetime}
              timeIncrements={PUBLISH_INTERVAL_MINUTES}
              onChange={date => setPublishDate(date)}
              min={momentLocalized.startOf("day").format("YYYY-MM-DD")}
              max={momentLocalized
                .startOf("day")
                .add(1, "year")
                .format("YYYY-MM-DD")}
            />

            <div className="publish-footer">
              <label className="schedule-confirm">
                <Checkbox
                  defaultValue={publishConfirmed}
                  onChange={value => setPublishConfirmed(value)}
                />
                {trans(
                  "I understand my part will be automatically published to my readers at the scheduled date and time."
                )}
              </label>

              <Button
                variant={ButtonVariant.PRIMARY}
                fullWidth={true}
                onClick={handleSchedule}
                disabled={!publishConfirmed || !isValidDate}
              >
                {trans("Schedule")}
              </Button>

              {scheduledPublishDatetime && (
                <Button
                  variant={ButtonVariant.SECONDARY}
                  fullWidth={true}
                  onClick={handleUnschedule}
                >
                  {trans("Unschedule")}
                </Button>
              )}
              {!!errorMessage && (
                <p className="text-alert title-action">{errorMessage}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

PublishModal.propTypes = {
  onPublish: PropTypes.func,
  scheduledPublishDatetime: PropTypes.string,
  onClose: PropTypes.func,
  triggerSchedulePart: PropTypes.bool,
  onUnschedule: PropTypes.func
};

export default PublishModal;
